<template>
  <div v-show="false" />
</template>

<script>
import Vue from 'vue'

export default Vue.extend({
  name: 'SseClient',
  props: {
    sseUrl: { type: String, required: true },
    autoReconnect: { type: Boolean, default: true },
    reconnectInterval: { type: Number, default: 3000 },
    queryParams: { type: Object, default: () => ({}) },
    headers: {
      type: Object,
      default: () => ({
        "Accept": "text/event-stream",
        "Accept-Charset": "UTF-8",
        "Cache-Control": "no-cache",
        "Pragma": "no-cache",
        "X-Requested-With": "XMLHttpRequest"
      })
    }
  },
  data() {
    return {
      responseStream: null,
      reader: null,
      reconnectTimer: null,
      isConnected: false,
      abortController: null,
      buffer: '', // 新增：缓冲区处理分段数据（对齐index.html）
      decoder: new TextDecoder("UTF-8", { fatal: true, ignoreBOM: true }) // 严格UTF-8解码
    }
  },
  // 移除mounted自动初始化（交给父组件手动调用，避免无参数请求）
  // mounted() { this.initSSE() },
  beforeDestroy() {
    this.closeSSE()
  },
  methods: {
    async initSSE() {
      if (!window.fetch || !window.ReadableStream) {
        this.$emit('error', new Error('当前浏览器不支持Fetch API/ReadableStream'))
        return
      }

      // 终止之前的请求和定时器（彻底清理）
      this.closeSSE()

      // 1. 拼接URL参数（关键：加encodeURIComponent，对齐index.html）
      const url = new URL(this.sseUrl, window.location.origin)
      Object.entries(this.queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // 核心修复：参数值编码，避免特殊字符截断
          url.searchParams.append(key, encodeURIComponent(value))
        }
      })

      try {
        this.abortController = new AbortController()

        // 2. 请求配置对齐index.html（加keepalive、完善headers）
        const response = await fetch(url.href, {
          method: 'GET',
          headers: new Headers(this.headers),
          // 移除或修改 credentials 配置
          credentials: 'omit', // 或者 'same-origin'，不要用 'include'
          signal: this.abortController.signal,
          keepalive: true,
          cache: 'no-store'
        })

        if (!response.ok) {
          throw new Error(`SSE请求失败，状态码：${response.status}`)
        }

        // 验证响应格式（对齐index.html）
        const contentType = response.headers.get("Content-Type")
        if (!contentType || !contentType.includes("text/event-stream")) {
          throw new Error("服务端未返回SSE流式数据")
        }

        this.isConnected = true
        clearTimeout(this.reconnectTimer)
        this.$emit('open', 'SSE连接成功')

        // 3. 流式读取逻辑（完全对齐index.html的缓冲区+多行解析）
        this.responseStream = response.body
        this.reader = this.responseStream.getReader()
        await this.readStream() // 启动流读取

        // 流正常结束
        this.isConnected = false
        if (this.autoReconnect) {
          this.triggerReconnect()
        }
      } catch (e) {
        this.isConnected = false
        this.$emit('error', e, 'SSE连接/读取异常')
        if (this.autoReconnect && e.name !== "AbortError") { // 忽略主动终止的错误
          this.triggerReconnect()
        }
      }
    },

    // 新增：完整的流读取逻辑（对齐index.html）
    async readStream() {
      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await this.reader.read()
          if (done) break

          // 分段解码+缓冲区拼接（解决半字符乱码）
          this.buffer += this.decoder.decode(value, { stream: true })
          // 按SSE标准分隔符分割（\n\n，兼容\r\n\n）
          const sseMsgList = this.buffer.split(/\r?\n\r?\n/).filter(Boolean)
          // 最后一个元素可能不完整，重新存入缓冲区
          this.buffer = sseMsgList.length > 0 ? sseMsgList.pop() : ""
          // 处理所有完整的SSE消息
          sseMsgList.forEach(this.handleSseData)
        }

        // 处理缓冲区最后剩余数据
        if (this.buffer) {
          this.handleSseData(this.buffer)
          this.buffer = ""
        }
      } catch (e) {
        if (e.name !== "AbortError") { // 忽略主动终止
          this.$emit('error', e, '流读取解码失败')
        }
      }
    },

    // 新增：规范解析SSE消息（对齐index.html，支持多行data、过滤注释）
    handleSseData(sseMsg) {
      if (!sseMsg || sseMsg.startsWith(":")) return // 过滤空行和SSE注释
      // 匹配所有data行（支持多行data：data: a\ndata: b）
      const dataLines = sseMsg.match(/^data:\s*(.+)$/gim)
      if (!dataLines) return
      // 拼接多行data
      const content = dataLines.map(line => {
        return line.replace(/^data:\s*/, "").trim()
      }).join("")
      // 排除服务端结束标记
      if (content && content !== '[DONE]') {
        this.$emit('message', content)
      }
    },

    // 触发重连（保留，但加防重复）
    triggerReconnect() {
      if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
      this.reconnectTimer = setTimeout(() => {
        this.$emit('reconnect', '开始SSE自动重连...')
        this.initSSE()
      }, this.reconnectInterval)
    },

    // 关闭SSE（强化清理逻辑）
    closeSSE() {
      this.isConnected = false
      clearTimeout(this.reconnectTimer)
      this.abortController?.abort()
      // 释放流读取器
      if (this.reader) {
        this.reader.releaseLock().catch(() => {})
        this.reader = null
      }
      this.responseStream = null
      this.abortController = null
      this.buffer = "" // 清空缓冲区
      this.$emit('close', 'SSE连接已手动关闭')
    },

    // 手动重连
    reconnectSSE() {
      this.closeSSE()
      setTimeout(() => this.initSSE(), 500)
    }
  },
  expose: ['onCustomEvent', 'closeSSE', 'reconnectSSE', 'initSSE']
})
</script>
